import { tilePoster } from "../library/posterSize.js";
import { useQueryClient } from "@tanstack/react-query";
import { alternateStreamUrls } from "../library/streamAlternates.js";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { Channel, SeriesItem, VodItem } from "@hybrid-iptv/types";
import { cn, EmptyState, Section, Spinner } from "@hybrid-iptv/ui";
import { useChannels } from "../epg/hooks.js";
import { useActiveProviderSourceId, activeProviderSourceId } from "../epg/api.js";
import { mirrorKeyFor } from "../library/mirrorResolver.js";
import { useMovieMatch, useProviderMovieDetails, useTmdbDetails } from "../library/vodHooks.js";
import { movieTitleFields } from "../library/titleFields.js";
import { useCatalogQuery } from "../library/useCatalogQuery.js";
import type { CatalogQuery } from "../library/catalogQuery.js";
import { TitleDetail } from "../library/TitleDetail.js";
import { FavoriteStar } from "../library/FavoriteStar.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { useFavoriteRefs } from "../library/useFavorites.js";
import { formatRemaining, isResumable, useProgressStore } from "../library/progressStore.js";
import { usePlayerStore } from "../player/playerStore.js";
import { tileLetter } from "../library/tileLetter.js";

const NO_MOVIES: VodItem[] = [];
const NO_SERIES: SeriesItem[] = [];

/**
 * My List (Section 11) — every favoritable thing in one place: channels that
 * tune straight away, movies with the detail overlay, and series that hand
 * off to the Series page for season/episode selection (same flow as search).
 * Removal is a press on the ★; nothing here navigates away from the list to
 * do it.
 */
export function MyListPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sourceId = useActiveProviderSourceId();
  const { data: channelData, isLoading: channelsLoading, isError: channelsError } = useChannels(sourceId);
  const channels = channelData?.channels ?? [];

  const favoritesHydrated = useFavoritesStore((s) => s.hydrated);
  const favMovieRefs = useFavoriteRefs("movie");
  const favSeriesRefs = useFavoriteRefs("series");
  const favChannelRefs = useFavoriteRefs("channel");
  const favMovieIds = useMemo(() => new Set(favMovieRefs), [favMovieRefs]);
  const favSeriesIds = useMemo(() => new Set(favSeriesRefs), [favSeriesRefs]);
  const favChannelIds = useMemo(() => new Set(favChannelRefs), [favChannelRefs]);

  // Only the favourites are asked for (stage B): on the device native SQLite
  // looks them up by id, so the page never loads the whole catalog for a list.
  const movieQuery = useMemo<CatalogQuery>(
    () => ({ kind: "vod", ids: favMovieRefs, limit: Math.max(favMovieRefs.length, 1) }),
    [favMovieRefs],
  );
  const seriesQuery = useMemo<CatalogQuery>(
    () => ({ kind: "series", ids: favSeriesRefs, limit: Math.max(favSeriesRefs.length, 1) }),
    [favSeriesRefs],
  );
  const movieResult = useCatalogQuery<VodItem>(sourceId, movieQuery, favMovieRefs.length > 0);
  const seriesResult = useCatalogQuery<SeriesItem>(sourceId, seriesQuery, favSeriesRefs.length > 0);
  const movies = (favMovieRefs.length > 0 && movieResult.data?.items) || NO_MOVIES;
  const series = (favSeriesRefs.length > 0 && seriesResult.data?.items) || NO_SERIES;
  const vodLoading = (favMovieRefs.length > 0 && movieResult.isLoading) || (favSeriesRefs.length > 0 && seriesResult.isLoading);
  const vodError = (favMovieRefs.length > 0 && movieResult.isError) || (favSeriesRefs.length > 0 && seriesResult.isError);

  // Resolve favorites against the catalogs, newest favorite first.
  const favChannels = useMemo(
    () => channels.filter((c) => favChannelIds.has(c.id)),
    [channels, favChannelIds],
  );
  const favMovies = useMemo(
    () =>
      [...movies]
        .filter((m) => favMovieIds.has(m.id))
        .sort((a, b) => favMovieRefs.indexOf(a.id) - favMovieRefs.indexOf(b.id)),
    [movies, favMovieIds, favMovieRefs],
  );
  const favSeries = useMemo(
    () =>
      [...series]
        .filter((s) => favSeriesIds.has(s.id))
        .sort((a, b) => favSeriesRefs.indexOf(a.id) - favSeriesRefs.indexOf(b.id)),
    [series, favSeriesIds, favSeriesRefs],
  );

  const hasAnyFavorite =
    favoritesHydrated && (favMovieRefs.length > 0 || favSeriesRefs.length > 0 || favChannelRefs.length > 0);

  // Movie detail overlay — same wiring as the Movies page so a favorite can be
  // played/resumed from here with the film's length (the panel's, else TMDB's) as the seek-bar length.
  const [selected, setSelected] = useState<VodItem | null>(null);
  const match = useMovieMatch(selected?.rawTitle ?? "", selected?.tmdbId);
  const details = useTmdbDetails("movie", match.data?.match?.tmdbId);
  // The panel's own details first (Section 26), as on the Movies page.
  const providerDetails = useProviderMovieDetails(selected);
  const fields = movieTitleFields(selected, providerDetails.data, details.data, match.data?.match?.summary);
  const progress = useProgressStore((s) => (selected ? s.get(selected.id) : undefined));
  const resumable = isResumable(progress);

  async function handlePlay(item: VodItem) {
    const saved = useProgressStore.getState().get(item.id);
    const store = usePlayerStore.getState();
    store.setPlayingContent({ contentType: "vod", contentRef: item.id });
    useProgressStore.getState().remember(item.id, { title: item.rawTitle, posterUrl: item.posterUrl, url: item.streamUrl });
    setSelected(null);
    const runtimeSeconds = fields.runtimeMinutes ? fields.runtimeMinutes * 60 : undefined;
    // Other streams of this same film, tried if this one will not play (duplicates.ts).
    const alternates = await alternateStreamUrls(queryClient, item);
    await store.playSource({
      url: item.streamUrl,
      type: "vod",
      title: item.rawTitle,
      artworkUrl: item.posterUrl,
      ...(runtimeSeconds ? { durationSeconds: runtimeSeconds } : {}),
      ...(alternates.length > 0 ? { altUrls: alternates } : {}),
    });
    if (isResumable(saved)) store.seek(saved!.positionSeconds);
  }

  function tuneChannel(channel: Channel) {
    const store = usePlayerStore.getState();
    store.pushRecent({ id: channel.id, name: channel.name, number: channel.number, streamUrl: channel.streamUrl, altStreamUrls: channel.altStreamUrls });
    void store.playSource({ url: channel.streamUrl, type: "live", title: channel.name, altUrls: channel.altStreamUrls, mirrorKey: mirrorKeyFor(activeProviderSourceId(), channel.id) });
  }

  const isLoading = channelsLoading || vodLoading;
  const isEmpty = favoritesHydrated && !hasAnyFavorite;
  // Favorites are saved on-device and would be lost from view if the catalog
  // fails to load — say so instead of rendering a bare page.
  const catalogFailed = hasAnyFavorite && !isLoading && (channelsError || vodError);

  return (
    <div className="page page--library">
      <Section title="My List">
        {/* It said "coming soon" and did nothing on OK — a dead button — long
            after the Catch-up page was built (health check, 2026-09-27). */}
        <button
          type="button"
          className="mylist-catchup-link"
          onClick={() => navigate("/catchup")}
          aria-label="Catch-up TV — watch what already aired"
        >
          <span className="mylist-catchup-link__icon" aria-hidden>↶</span>
          <span>
            <strong>Catch-up TV</strong>
            <small>Watch what already aired, channel by channel</small>
          </span>
        </button>
        {isEmpty && (
          <EmptyState
            icon="★"
            title="Your list is empty"
            body="Press the ★ on any movie, series, or channel — in search, the guide, or the library — and it shows up here."
          />
        )}
        {!isEmpty && isLoading && hasAnyFavorite && (
          <div className="library-grid library-grid--loading"><Spinner /> Loading your catalog…</div>
        )}
        {catalogFailed && (
          <EmptyState
            icon="⚠️"
            title="Couldn't load your list"
            body="Your favorites are saved on this device, but the catalog failed to load. Check the source connection in Settings, then try again."
          />
        )}

        {!isEmpty && favChannels.length > 0 && (
          <h3 className="mylist-heading">Favorite Channels ({favChannels.length})</h3>
        )}
        {!isEmpty && favChannels.length > 0 && (
          <div className="mylist-channels">
            {favChannels.map((channel) => (
              <div key={channel.id} className="mylist-channel">
                <button
                  type="button"
                  className="mylist-channel__main"
                  onClick={() => tuneChannel(channel)}
                  aria-label={`Watch ${channel.name}`}
                >
                  {channel.logoUrl ? (
                    <img className="mylist-channel__logo" src={channel.logoUrl} alt="" loading="lazy" />
                  ) : (
                    <span className="mylist-channel__fallback">{tileLetter(channel.name, "?")}</span>
                  )}
                  <span className="mylist-channel__name">{channel.name}</span>
                </button>
                <FavoriteStar kind="channel" ref={channel.id} label={channel.name} />
              </div>
            ))}
          </div>
        )}

        {!isEmpty && favMovies.length > 0 && (
          <h3 className="mylist-heading">Favorite Movies ({favMovies.length})</h3>
        )}
        {!isEmpty && favMovies.length > 0 && (
          <div className="library-grid library-grid--stacked">
            {favMovies.map((movie) => (
              <div key={movie.id} className="library-card-wrap">
                <button
                  type="button"
                  className="library-card"
                  onClick={() => setSelected(movie)}
                  aria-label={movie.rawTitle}
                >
                  {movie.posterUrl ? (
                    <img className="library-card__art" src={tilePoster(movie.posterUrl)} alt={movie.rawTitle} loading="lazy" decoding="async" />
                  ) : (
                    <span className={cn("library-card__fallback", movie.categoryId)}>
                      {tileLetter(movie.rawTitle, "?")}
                    </span>
                  )}
                  <span className="library-card__title">{movie.rawTitle}</span>
                  {isResumable(useProgressStore.getState().get(movie.id)) && (
                    <span className="library-card__resume">● Resume</span>
                  )}
                </button>
                <FavoriteStar kind="movie" ref={movie.id} label={movie.rawTitle} className="fav-star--left" />
              </div>
            ))}
          </div>
        )}

        {!isEmpty && favSeries.length > 0 && (
          <h3 className="mylist-heading">Favorite Series ({favSeries.length})</h3>
        )}
        {!isEmpty && favSeries.length > 0 && (
          <div className="library-grid library-grid--series library-grid--stacked">
            {favSeries.map((item) => (
              <div key={item.id} className="library-card-wrap">
                <button
                  type="button"
                  className="library-card library-card--series"
                  onClick={() => navigate(`/series?q=${encodeURIComponent(item.rawTitle)}`)}
                  aria-label={item.rawTitle}
                >
                  {item.posterUrl ? (
                    <img className="library-card__art" src={tilePoster(item.posterUrl)} alt={item.rawTitle} loading="lazy" decoding="async" />
                  ) : (
                    <span className={cn("library-card__fallback", item.categoryId)}>
                      {tileLetter(item.rawTitle, "S")}
                    </span>
                  )}
                  <span className="library-card__title">{item.rawTitle}</span>
                </button>
                <FavoriteStar kind="series" ref={item.id} label={item.rawTitle} className="fav-star--left" />
              </div>
            ))}
          </div>
        )}
      </Section>

      <TitleDetail
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.rawTitle ?? ""}
        subtitle={fields.subtitle}
        overview={fields.overview}
        posterUrl={fields.posterUrl}
        backdropUrl={fields.backdropUrl}
        rating={fields.rating}
        genres={fields.genres}
        director={fields.director}
        trailerUrl={fields.trailerUrl}
        status={{ kind: "streamable", detail: "In your playlist" }}
        onPlay={selected ? () => void handlePlay(selected) : undefined}
        playLabel={resumable && progress ? `▶ Resume (${formatRemaining(progress)})` : "▶ Play"}
      />
    </div>
  );
}
